<script setup lang="ts">
import { ChevronDown } from 'lucide-vue-next'
import type { Component } from 'vue'
import { onMounted, ref, watch } from 'vue'
import {
  Combobox,
  ComboboxAnchor,
  ComboboxEmpty,
  ComboboxGroup,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  ComboboxTrigger,
} from '@/foundation/combobox'
import { adjustHexLightness, hexToRgb, rgbToHsl } from '@/utils/utils'

/**
 * Option list items: each has an id, a Vue component (e.g. SVG icon)
 * and a user-facing name.
 */
export type OptionType = {
  id: string
  component: Component | string
  name: string
  color?: string
}

const props = defineProps({
  options: { type: Array as () => OptionType[], required: true },
  disabled: { type: Boolean, default: false },
  value: { type: Object as () => OptionType | null, default: null },
  label: { type: String, default: '' },
  emptyMessage: { type: String, default: 'No results found.' },
  placeholder: { type: String, default: '' },
  insideText: { type: String, default: '' },
  defaultOption: { type: String, default: null },
  padding: { type: String, default: '' },
  height: { type: Number, default: 40 },
  color: { type: String, default: 'currentColor' },
})

const emit = defineEmits<{
  (e: 'update:value', payload: OptionType | null): void
}>()

/** Used to force re-mount when value cleared */
const key = ref(0)

/** On select, emit the full OptionType chosen */
const handleSelect = (ev: CustomEvent) => {
  const selected = ev.detail.value as OptionType

  emit('update:value', selected)
}

/** Display text in input: label prop has priority, sinon use selected name */
const displayValue = (val: OptionType | null) => {
  if (!val) return ''

  return props.label || val.name
}

// Initial defaultOption if provided (match by name or id)
onMounted(() => {
  if (props.defaultOption) {
    const def = props.options.find((o) => o.name === props.defaultOption || o.id === props.defaultOption)

    if (def) emit('update:value', def)
  }
})

// Force reset if value becomes null or undefined
watch(
  () => props.value,
  (newVal) => {
    if (!newVal) key.value++
  },
  { immediate: true },
)
const generateDarkerColor = (color: string) => {
  if (!color) return 'hsl(0, 100%, 100%)' // Fallback to black if no color provided
  const regex = /hsl\(\s*([\d.]+)\s*,\s*([\d.]+)%\s*,\s*([\d.]+)%\s*\)/
  const match = color.match(regex)
  const hue = Number(match?.length && match[1]) // 55
  const saturation = Number(match?.length && match[2]).toFixed(2) // 96.7

  const lightness = Number(match?.length && match[3]).toFixed(2) // 88

  const factor = 0.5 // Adjust this factor to make the color darker or lighter

  return `hsl(${hue}, ${parseFloat(saturation) * factor}%, ${parseFloat(lightness) * factor}%)`
}
const computeColor = (color: string) => {
  const { r, g, b } = hexToRgb(color)

  const { h, s, l } = rgbToHsl(r, g, b)

  return generateDarkerColor('hsl(' + h + ',' + s + '%,' + l + '%)')
}
</script>

<template>
  <Combobox :key="key" by="id" :disabled="disabled">
    <ComboboxAnchor class="w-full" :style="{ '--combobox-anchor-width': '100%' }">
      <ComboboxTrigger as-child :disabled="disabled">
        <div class="relative w-full flex items-center">
          <!-- Selected icon default size -->
          <div
            v-if="typeof value.component === 'string'"
            class="h-5 w-5 rounded-full size-5 absolute left-3 top-1/2 -translate-y-1/2"
            :style="{ backgroundColor: adjustHexLightness(value.component, 1.2) }"
          />
          <component :is="value.component" v-else-if="value" class="size-5 absolute left-3 top-1/2 -translate-y-1/2" />
          <ComboboxInput
            :class="{ 'pl-10': value?.id }"
            :style="{ height: height + 'px', color: '#71717A' }"
            :display-value="() => displayValue(value ?? null)"
            :placeholder="placeholder"
          />
          <span class="absolute inset-y-0 end-0 flex items-center justify-center px-3">
            <ChevronDown class="w-6 h-6 text-muted-foreground cursor-pointer" />
          </span>
        </div>
      </ComboboxTrigger>
    </ComboboxAnchor>

    <ComboboxList v-if="options.length" class="w-full">
      <ComboboxEmpty class="w-full">
        {{ emptyMessage }}
      </ComboboxEmpty>

      <!-- Horizontal scrollable list with hidden scrollbar -->
      <ComboboxGroup class="flex flex-nowrap overflow-x-auto space-x-2 p-2">
        <span v-if="insideText" class="block text-xs font-semibold text-muted-foreground mb-1 mt-1">
          {{ insideText }}
        </span>
        <ComboboxItem
          v-for="option in options"
          :key="option.id"
          :value="option"
          class="flex-none p-1 cursor-pointer rounded-full"
          :style="`color: ${computeColor(option.component.toString())};`"
          @select="handleSelect"
        >
          <!-- Enlarged icons in the options list -->
          <div
            v-if="typeof option.component === 'string'"
            class="swatch h-5 w-5 rounded-full relative !ring-0"
            :style="`color: ${adjustHexLightness(option.component, 1.5)} !important; background-color: ${adjustHexLightness(option.component, 1.2)}; border: 2px solid ${value && option.id === value.id ? adjustHexLightness(option.component, 1.5) : 'inherit'}; `"
          />
          <component
            :is="option.component"
            v-else
            class="w-8 h-8"
            :style="{ stroke: value && option.id === value.id ? '#00ff0054' : undefined, strokeWidth: 1.2 }"
          />
        </ComboboxItem>
      </ComboboxGroup>
    </ComboboxList>
  </Combobox>
</template>

<style scoped>
[data-reka-popper-content-wrapper] {
  pointer-events: auto !important;
  z-index: 9999 !important;
  min-width: var(--reka-popper-anchor-width) !important;
  width: var(--reka-popper-anchor-width) !important;
}
/* Hide scrollbar */
.hide-scrollbar::-webkit-scrollbar {
  display: none;
}
.hide-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
[data-reka-popper-content-wrapper] > div {
  width: 100% !important;
}
[data-highlighted] .swatch {
  border-color: currentColor !important;
  border-width: 2px;
}
</style>
